// dashboard-host.component.ts
import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';

import { KpiCardComponent } from '../components/kpi-card.component';
import { ChartWidgetComponent } from '../components/chart-widget.component';
import { TableWidgetComponent } from '../components/table-widget.component';

import {
  DashboardDetails,
  DashboardResponse,
  WidgetRow,
} from '../Services/widget.models';
import {
  DashboardResolverService,
  ResolvedWidget,
} from '../Services/dashboard-resolver.service';
import { environment } from 'src/environments/environment';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MaterialModule } from 'src/app/material.module';
import { MapWidgetComponent } from './map-widget.component';
import { TokenService } from 'src/app/core/services/token.service';
import { DshService } from 'src/app/modules/dsh/services/dsh.service';

@Component({
  selector: 'app-dashboard-host',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatCardModule,
    KpiCardComponent,
    ChartWidgetComponent,
    TableWidgetComponent,
    MatIcon,
    TranslateModule,
    MatButtonModule,
    MaterialModule,
    MapWidgetComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Loading state -->

    <div class="dh-spinner" *ngIf="loading">
      <mat-spinner diameter="48"></mat-spinner>
    </div>

    <!-- Page Header -->
    <mat-card class="cardWithShadow m-16 card" *ngIf="!loading && isAuthorized">
      <mat-card-content>
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center gap-8">
            <mat-icon
              class="text-primary"
              style="font-size:28px;width:28px;height:28px;"
              >dashboard</mat-icon
            >
            <div>
              <h2 class="m-0 title">{{ dashboardTitle }}</h2>
            </div>
          </div>
          <!-- <button mat-stroked-button color="accent" (click)="goBack()">
            <mat-icon>arrow_back</mat-icon>
            {{ 'Shared.GoBack' | translate }}
          </button> -->
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Dashboard grid -->
    <div
      class="dh-grid"
      *ngIf="!loading && resolvedWidgets.length && isAuthorized"
    >
      <ng-container *ngFor="let w of resolvedWidgets">
        <!-- KPI Card -->
        <div
          class="dh-cell"
          *ngIf="w.widgetTypeCode === 'KPI' && w.kpiConfig"
          [style.grid-column]="gridSpan(w.gridCol, w.gridWidth)"
          [style.grid-row]="gridSpan(w.gridRow, w.gridHeight)"
        >
          <app-kpi-card [config]="w.kpiConfig"></app-kpi-card>
        </div>

        <!-- Charts -->
        <div
          class="dh-cell"
          *ngIf="isChart(w.widgetTypeCode) && w.chartConfig"
          [style.grid-column]="gridSpan(w.gridCol, w.gridWidth)"
          [style.grid-row]="gridSpan(w.gridRow, w.gridHeight)"
        >
          <app-chart-widget [config]="w.chartConfig"></app-chart-widget>
        </div>

        <!-- Table -->
        <div
          class="dh-cell"
          *ngIf="
            (w.widgetTypeCode === 'TABLE' ||
              w.widgetTypeCode === 'ADVANCEDTABLE') &&
            w.tableConfig
          "
          [style.grid-column]="gridSpan(w.gridCol, w.gridWidth)"
          [style.grid-row]="gridSpan(w.gridRow, w.gridHeight)"
        >
          <app-table-widget [config]="w.tableConfig"></app-table-widget>
        </div>

        <div
          class="dh-cell"
          *ngIf="isMap(w.widgetTypeCode) && w.mapConfig"
          [style.grid-column]="gridSpan(w.gridCol, w.gridWidth)"
          [style.grid-row]="gridSpan(w.gridRow, w.gridHeight)"
        >
          <app-map-widget [config]="w.mapConfig"></app-map-widget>
        </div>
      </ng-container>
    </div>

    <!-- Empty -->
    <div
      class="dh-empty"
      *ngIf="!loading && !resolvedWidgets.length && isAuthorized"
    >
      No widgets configured for this dashboard.
    </div>

    <ng-container *ngIf="!loading && !isAuthorized">
      <mat-card class="cardWithShadow m-16 unauthorized-card">
        <mat-card-content
          class="d-flex flex-column align-items-center justify-content-center text-center"
        >
          <mat-icon class="unauthorized-icon">lock</mat-icon>
          <h3 class="m-0">{{ 'Shared.Unauthorized' | translate }}</h3>
          <p class="m-0 unauthorized-text">
            {{ 'Dsh.UnauthorizedDashboardMessage' | translate }}
          </p>
        </mat-card-content>
      </mat-card>
    </ng-container>
  `,
  styles: [
    `
      :host {
        display: block;
        background-color: var(--dsh-bg, transparent) !important;
        padding: 1%;
      }
      .dh-spinner {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 60px 0;
      }

      .dh-grid {
        background-color: var(--dsh-bg, transparent) !important;
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        grid-auto-rows: 80px;
        gap: 2px;
        padding: 20px;
        color: var(--dsh-text, inherit);
      }
      .card {
        background-color: var(--dsh-primary, #fff) !important;
      }
      .title {
        color: var(--dsh-text, #202020);
      }
      .dh-cell {
        min-width: 0;
        width: 100%;
        height: 100%;
      }

      .dh-empty {
        text-align: center;
        padding: 60px;
        color: #9aa5b4;
        font-size: 15px;
      }

      .unauthorized-card {
        padding: 40px 20px;
      }
      .unauthorized-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        color: #b0b7c3;
        margin-bottom: 12px;
      }
      .unauthorized-text {
        color: #9aa5b4;
        font-size: 14px;
        margin-top: 8px;
      }
    `,
  ],
})
export class DashboardHostComponent implements OnInit, OnChanges {
  /** Dashboard ID to load */
  @Input() dashboardId!: number;
  /** Language code for bilingual labels */
  @Input() langCode: 'en' | 'ar' = 'en';

  loading = false;
  resolvedWidgets: ResolvedWidget[] = [];
  AuthorizedRoles: any;
  UserRoles: any;
  apiUrl = environment.apiUrl;
  isAuthorized: boolean;
  constructor(
    private http: HttpClient,
    private resolver: DashboardResolverService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private renderer: Renderer2,
    private translate: TranslateService,
    private token: TokenService,
    private dashboardService: DshService
  ) {
    let lang: any = localStorage.getItem('lang');
    let langObj: any;
    if (lang != undefined && lang != null) {
      langObj = JSON.parse(lang);
      this.langCode = langObj.code;
    } else {
      this.langCode = 'ar';
    }

    this.translate.use(this.langCode);
    this.UserRoles = this.token.getUserRolesFromToken();
  }

  ngOnInit(): void {
    this.load();
    this.isAuthorizedUser();
  }
  ngOnChanges(c: SimpleChanges): void {
    if (!c['dashboardId']?.firstChange) this.load();
  }
  dashboardTitle: any;

  isAuthorizedUser(): void {
    if (!this.UserRoles?.length || !this.AuthorizedRoles?.length) {
      this.isAuthorized = false;
      return;
    }
    this.isAuthorized = this.AuthorizedRoles.some(
      (x: any) => x.roleAuthorityIsActive && this.UserRoles.includes(x.roleId),
    );
  }

  private load(): void {
    if (!this.dashboardId) return;
    this.loading = true;
    this.cdr.markForCheck();

    const payload = {
      filter: null,
      pageSize: 10000,
      currentPage: 1,
      data: { dashboardId: this.dashboardId },
    };

    // this.http
    //   .post<DashboardResponse>(
    //     `${this.apiUrl}/dsh/dashboard/details-with-data`,
    //     payload,
    //   )
    //   .pipe(
    //     catchError((err) => {
    //       console.error('Dashboard load error', err);
    //       return of(null);
    //     }),
    //   )
    this.dashboardService.getDashboardWithData(payload)
      .subscribe((result: any) => {
        this.loading = false;
        if (result) {
          this.resolvedWidgets = this.resolver.resolve(
            result.structure,
            result.widgetData,
            this.langCode,
          );
          this.applyTheme(result.structure);
          this.dashboardTitle =
            this.langCode == 'ar'
              ? result.structure?.dashboards[0]?.nameAr
              : result.structure?.dashboards[0]?.nameEn;

          this.AuthorizedRoles =
            result.structure.dashboardAuthorityRoles.filter(
              (x: any) => x.dashboardId === this.dashboardId,
            );

          this.isAuthorizedUser();

        }
        this.cdr.markForCheck();
      });
  }

  /** Convert gridCol + gridWidth into CSS grid-column span string */
  gridSpan(start: number, span: number): string {
    return `${start + 1} / span ${span}`;
  }

  isChart(code: string): boolean {
    return [
      'PIE',
      'DOUGHNUT',
      'LINE',
      'BAR',
      'HBAR',
      'MULTIPLE_LINES',
      'STACKED_BAR',
    ].includes(code);
  }

  isMap(code: string): boolean {
    return code === 'MAP_V';
  }
  private applyTheme(structure: DashboardDetails): void {
    const dash = structure.dashboards?.[0];
    if (!dash) return;

    const host = this.el.nativeElement as HTMLElement;

    if (dash.primaryColor)
      host.style.setProperty('--dsh-primary', dash.primaryColor);
    if (dash.accentColor)
      host.style.setProperty('--dsh-accent', dash.accentColor);
    if (dash.backgroundColor)
      host.style.setProperty('--dsh-bg', dash.backgroundColor || 'transparent');
    if (dash.textColor) host.style.setProperty('--dsh-text', dash.textColor);
    if (dash.chartColorArray) {
      try {
        const colors: string[] = JSON.parse(dash.chartColorArray);
        colors.forEach((c, i) => host.style.setProperty(`--dsh-chart-${i}`, c));
      } catch {
        /* malformed JSON — skip */
      }
    }
  }
  goBack(): void {
    window.history.back();
  }
}
